<template>
  <SidebarFieldGrid :columns="1">
    <SidebarFieldRow label="Ursachen">
      <AppSelect
        v-model="formVariables.ursacheSelected"
        sort="none"
        :options="ursachenOptions"
        text-field="label"
        value-field="enum"
        label="Ursache"
        placeholder="Ursache auswählen"
        clearable
        class="fvc-bearbeitung__select"
        @change="checkIfDataWasChangedAndCommit"
      />
    </SidebarFieldRow>
    <SidebarFieldRow label="Beschreibung">
      <b-form-input
        v-model="formVariables.beschreibungText"
        @input="checkIfDataWasChangedAndCommit"
        class="fvc-bearbeitung__input"
        placeholder="Beschreibung einfügen..."
      ></b-form-input>
      <button
        v-if="formVariables.beschreibungText"
        type="button"
        class="btn btn-icon icon-button fvc-bearbeitung__reset"
        title="Beschreibung leeren"
        @click="resetBeschreibung"
      >
        <X :size="14" />
      </button>
    </SidebarFieldRow>
    <SidebarFieldRow label="Bearbeitungsstatus">
      <AppSelect
        v-model="formVariables.statusSelected"
        sort="none"
        :options="statusOptions"
        text-field="label"
        value-field="enum"
        label="Status"
        placeholder="Status auswählen"
        hide-search
        clearable
        class="fvc-bearbeitung__select"
        @change="onStatusChange"
      />
    </SidebarFieldRow>
    <SidebarFieldRow
      v-if="
        (data.bearbeitungStatus === 'VerfuegbarkeitGeschaffen' && formVariables.statusSelected === null) ||
        formVariables.statusSelected === 'VerfuegbarkeitGeschaffen'
      "
      label="Lösungsansatz"
    >
      <AppSelect
        v-model="formVariables.loesungsansatzSelected"
        sort="none"
        :options="loesungsansatzOptions"
        text-field="label"
        value-field="enum"
        label="Lösungsansatz"
        placeholder="Lösungsansatz auswählen"
        hide-search
        clearable
        class="fvc-bearbeitung__select"
        @change="checkIfDataWasChangedAndCommit"
      />
    </SidebarFieldRow>
    <SidebarFieldRow
      v-if="
        (data.bearbeitungStatus === 'Akzeptiert' && formVariables.statusSelected === null) ||
        formVariables.statusSelected === 'Akzeptiert'
      "
      label="Akzeptanzgrund"
    >
      <AppSelect
        v-model="formVariables.akzeptanzgrundSelected"
        sort="none"
        :options="akzeptanzgrundOptions"
        :state="formVariables.akzeptanzgrundSelected !== null"
        text-field="label"
        value-field="enum"
        label="Akzeptanzgrund"
        placeholder="Akzeptanzgrund auswählen"
        clearable
        class="fvc-bearbeitung__select"
        @change="checkIfDataWasChangedAndCommit"
      />
    </SidebarFieldRow>
    <SidebarFieldRow label="Bearbeitende Person" readonly>
      {{ data.lastModifiedUserId ? this.$store.getters.getUsernameById(data.lastModifiedUserId) : '-' }}
    </SidebarFieldRow>
    <SidebarFieldRow label="Bearbeitungsdatum" readonly :last="last">
      {{ data.bearbeitungsdatum ? this.dateFormatterStandard(data.bearbeitungsdatum) : '-' }}
    </SidebarFieldRow>
  </SidebarFieldGrid>
</template>
<style scoped>
.fvc-bearbeitung__select {
  --app-select-height: var(--control-h-sm);
  --app-select-padding-x: 8px;
  --app-select-font-size: 13px;
}
.fvc-bearbeitung__input {
  height: var(--control-h-sm);
  padding: 4px 8px;
  font-size: 13px;
  font-weight: 500;
  border-radius: var(--radius-sm);
}
.fvc-bearbeitung__reset {
  flex-shrink: 0;
  width: 32px;
  height: 32px;
}
</style>
<script>
import { format, parseISO } from 'date-fns';
import { mapState } from 'vuex';
import {
  ursachenPossibilities,
  loesungsansatzOptions,
  akzeptanzgrundPossibilities,
  allAkzeptanzgruende,
  editStatusOptions,
} from '@/core/flugverfuegbarkeit/bearbeitungs-options.config';
import { SET_DATAWASCHANGED } from '@/core/flugverfuegbarkeit/stores/flugverfuegbarkeit.module';
import { X } from 'lucide-vue';
import AppSelect from '@/components/common/app-select/app-select.vue';
import SidebarFieldGrid from '@/components/common/sidebar-section/sidebar-field-grid.vue';
import SidebarFieldRow from '@/components/common/sidebar-section/sidebar-field-row.vue';
export default {
  components: {
    X,
    AppSelect,
    SidebarFieldGrid,
    SidebarFieldRow,
  },
  props: {
    // Letzte Zeile folgt der Kartenrundung, wenn darunter kein Hinweis mehr steht
    last: { type: Boolean, default: true },
  },
  data() {
    return {
      labels: [
        'Ursachen',
        'Beschreibung',
        'Verfügbarkeit geschaffen',
        'Lösungsansatz',
        'Akzeptiert',
        'Grund Akzeptanz',
        'Bearbeitende Person',
      ],
      loesungsansatzOptions,
      statusOptions: editStatusOptions,
      formVariables: {
        ursacheSelected: null,
        loesungsansatzSelected: null,
        akzeptanzgrundSelected: null,
        akzeptiertStatus: false,
        beschreibungText: null,
        statusSelected: null,
      },
    };
  },
  watch: {
    data(newData) {
      this.initializeFormVariables();
      this.checkIfDataWasChangedAndCommit();
    },
  },
  computed: {
    ...mapState({
      data: state => state.flugverfuegbarkeit.detailData,
      dataWasChanged: state => state.flugverfuegbarkeit.bearbeitung.dataWasChanged,
    }),
    akzeptanzgrundOptions() {
      if (!this.data.status) return [];
      const options = akzeptanzgrundPossibilities.find(item => item.status === this.data.status).options;
      // Ein gespeicherter, nicht mehr auswählbarer Grund wird angezeigt, aber gesperrt angehängt -> somit nicht mehr auswählbar
      const inaktiverGrund =
        this.data.akzeptanzgrund && !options.some(option => option.enum === this.data.akzeptanzgrund)
          ? allAkzeptanzgruende.find(option => option.enum === this.data.akzeptanzgrund)
          : null;
      return inaktiverGrund ? [...options, { ...inaktiverGrund, disabled: true }] : options;
    },
    ursachenOptions() {
      if (this.data.status) {
        return ursachenPossibilities.find(item => item.status === this.data.status).options;
      } else return [];
    },
  },
  methods: {
    // Lösungsansatz und Akzeptanzgrund hängen am Status und werden bei dessen Wechsel geleert
    onStatusChange() {
      this.formVariables.akzeptanzgrundSelected = null;
      this.formVariables.loesungsansatzSelected = null;
      this.checkIfDataWasChangedAndCommit();
    },
    // Die Felder halten den Enum-Wert, deshalb werden die gespeicherten Werte direkt übernommen
    initializeFormVariables() {
      this.formVariables.ursacheSelected = this.data.ursache || null;
      this.formVariables.statusSelected = this.data.bearbeitungStatus || null;
      this.formVariables.loesungsansatzSelected = this.data.loesungsansatz || null;
      // Der Akzeptanzgrund gehört zu einem Status, ohne diesen bleibt er leer
      this.formVariables.akzeptanzgrundSelected =
        this.data.akzeptanzgrund && this.data.status ? this.data.akzeptanzgrund : null;
      this.formVariables.beschreibungText = this.data.beschreibung || null;
    },

    checkIfDataWasChangedAndCommit() {
      const statusChanged = this.data?.bearbeitungStatus != this.formVariables.statusSelected;
      const ursacheChanged = this.data?.ursache != this.formVariables.ursacheSelected;
      const beschreibungChanged = this.data.beschreibung != this.formVariables.beschreibungText;
      const akzeptanzgrundChanged = this.data.akzeptanzgrund != this.formVariables.akzeptanzgrundSelected;
      const loesungsansatzChanged = this.data.loesungsansatz != this.formVariables.loesungsansatzSelected;

      const dataChanged =
        statusChanged ||
        ursacheChanged ||
        beschreibungChanged ||
        akzeptanzgrundChanged ||
        loesungsansatzChanged;

      this.$store.commit('flugverfuegbarkeit/' + SET_DATAWASCHANGED, dataChanged);
      this.$emit('data-was-changed', this.formVariables);

      return dataChanged;
    },
    akzeptiertStatusChanged() {
      if (!this.formVariables.akzeptiertStatus) {
        this.formVariables.akzeptanzgrundSelected = null;
      }
      this.checkIfDataWasChangedAndCommit();
    },
    resetBeschreibung() {
      this.formVariables.beschreibungText = null;
      this.checkIfDataWasChangedAndCommit();
    },
    dateFormatterStandard(date) {
      return format(parseISO(date), 'dd.MM.yyyy');
    },
  },
};
</script>
